import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setApprovalConfiguration } from '../../../../redux/slices/LomavisPostApproval/lomavisPostApproval';
import BasicQuillEditor from '../../../generic/basicQuillEditor';
import { useTranslate } from '@tolgee/react';

export const APPROVAL_TEXT_EDITOR_TARGET_PUBLIC_MESSAGE = 'public_message';
export const APPROVAL_TEXT_EDITOR_TARGET_PRIVATE_MESSAGE = 'private_message';
export const APPROVAL_MESSAGE_MAX_LENGTH = 1000;

interface ApprovalQuillTextProps {
  target: string;
}

const getApprovalMessageLength = (quillEditor: any): number => {
  if (!quillEditor) return 0;

  const text = quillEditor.getText() || '';
  return text.endsWith('\n') ? text.length - 1 : text.length;
};

export const ApprovalQuillText: React.FC<ApprovalQuillTextProps> = ({
  target
}) => {
  const { t } = useTranslate();
  const {
    data: { public_message, private_message }
  } = useSelector((state: any) => state.lomavisPostApproval);
  const [quillEditor, setQuillEditor] = useState(null);
  const [characterCount, setCharacterCount] = useState(0);
  const [showCharacterLimitFeedback, setShowCharacterLimitFeedback] =
    useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (quillEditor) {
      quillEditor.off('text-change');

      quillEditor.setContents(
        target === APPROVAL_TEXT_EDITOR_TARGET_PUBLIC_MESSAGE
          ? public_message
          : private_message
      );
      setCharacterCount(getApprovalMessageLength(quillEditor));
      setShowCharacterLimitFeedback(
        getApprovalMessageLength(quillEditor) > APPROVAL_MESSAGE_MAX_LENGTH
      );

      quillEditor.on('text-change', () => {
        let nextCharacterCount = getApprovalMessageLength(quillEditor);

        if (nextCharacterCount > APPROVAL_MESSAGE_MAX_LENGTH) {
          setShowCharacterLimitFeedback(true);
          quillEditor.deleteText(
            APPROVAL_MESSAGE_MAX_LENGTH,
            nextCharacterCount - APPROVAL_MESSAGE_MAX_LENGTH,
            'silent'
          );
          quillEditor.setSelection(APPROVAL_MESSAGE_MAX_LENGTH, 0, 'silent');
          nextCharacterCount = getApprovalMessageLength(quillEditor);
        } else if (nextCharacterCount < APPROVAL_MESSAGE_MAX_LENGTH) {
          setShowCharacterLimitFeedback(false);
        }

        setCharacterCount(nextCharacterCount);
        dispatch(
          setApprovalConfiguration({
            key: target,
            value: quillEditor.getContents()
          })
        );
      });
    }
  }, [quillEditor, target]);

  const overCharacterLimit = characterCount > APPROVAL_MESSAGE_MAX_LENGTH;
  const showLimitFeedback = overCharacterLimit || showCharacterLimitFeedback;

  return (
    <>
      <BasicQuillEditor
        quillEditor={quillEditor}
        setQuillEditor={setQuillEditor}
        editorStyle={{}}
        readOnly={false}
      />
      <div
        className={`d-flex justify-content-end small mt-2 ${
          showLimitFeedback ? 'text-danger' : 'text-muted'
        }`}
      >
        {characterCount}/{APPROVAL_MESSAGE_MAX_LENGTH}
      </div>
      {showLimitFeedback && (
        <div className="text-danger small mt-1">
          {t('lomavisPostApproval.approvalMessageCard.characterLimitExceeded', {
            limit: APPROVAL_MESSAGE_MAX_LENGTH
          })}
        </div>
      )}
    </>
  );
};
